import { useState, useMemo } from "react";
import { View, StyleSheet, ScrollView, Pressable } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { AttachmentFile } from "@/components/Attachment/types";
import { FileItem } from "../components/FileItem";
import { FileViewProps } from "../types";

type SortField = "name" | "size" | "category";
type SortDirection = "asc" | "desc";

interface SortableHeaderProps {
  label: string;
  field: SortField;
  sortField: SortField | null;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  style?: any;
  align?: "left" | "center" | "right";
}

function SortableHeader({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  style,
  align = "left",
}: SortableHeaderProps) {
  const { colors } = useTheme();
  const isActive = sortField === field;
  const [hovered, setHovered] = useState(false);

  return (
    <Pressable
      onPress={() => onSort(field)}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      style={[
        styles.sortableHeader,
        style,
        { justifyContent: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start" },
      ]}
    >
      <ThemedText
        style={[
          styles.headerText,
          { color: isActive ? colors.text : colors.textSecondary },
          isActive && { fontWeight: "700" },
          hovered && !isActive && { color: colors.text },
        ]}
      >
        {label}
      </ThemedText>
      {isActive && (
        <MaterialCommunityIcons
          name={sortDirection === "asc" ? "chevron-up" : "chevron-down"}
          size={14}
          color={colors.text}
          style={styles.sortIcon}
        />
      )}
    </Pressable>
  );
}

export function ListView({ files, onFileRemove, onFileRename, onFilePreview }: FileViewProps) {
  const { colors } = useTheme();
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction or clear if already desc
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortField(null);
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedFiles = useMemo(() => {
    if (!sortField) return files;

    return [...files].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle null/undefined
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // String comparison for name/category
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = (bVal as string).toLowerCase();
      }

      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === "desc" ? -cmp : cmp;
    });
  }, [files, sortField, sortDirection]);

  return (
    <View style={styles.container}>
      {/* Header Row */}
      <View style={[styles.header, { borderColor: colors.border }]}>
        <View style={styles.headerIcon} />
        <SortableHeader
          label="Name"
          field="name"
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          style={styles.headerName}
        />
        <SortableHeader
          label="Size"
          field="size"
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          style={styles.headerSize}
          align="right"
        />
        <SortableHeader
          label="Type"
          field="category"
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          style={styles.headerType}
          align="center"
        />
        <View style={styles.headerActions} />
      </View>

      {/* File List */}
      <ScrollView style={styles.scrollView}>
        {sortedFiles.map((file) => (
          <FileItem
            key={file.id}
            file={file}
            mode="list"
            onRemove={onFileRemove ? () => onFileRemove(file.id) : undefined}
            onRename={onFileRename ? (newName) => onFileRename(file.id, newName) : undefined}
            onPreview={onFilePreview ? () => onFilePreview(file) : undefined}
          />
        ))}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    gap: 12,
  },
  headerIcon: {
    width: 40,
  },
  sortableHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  headerText: {
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
  },
  sortIcon: {
    marginLeft: 2,
  },
  headerName: {
    flex: 1,
  },
  headerSize: {
    width: 80,
  },
  headerType: {
    width: 70,
  },
  headerActions: {
    width: 30,
  },
  scrollView: {
    flex: 1,
  },
});
